import React from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { useAuthContext } from './context/AuthContext';
import { evalRoutes } from './eval/routes';
import { ForgeLayout } from './forge/components/layout/ForgeLayout';
import { MasterDataCollectionPage } from './forge/pages/admin/MasterDataCollectionPage';
import { MasterLabelPage } from './forge/pages/admin/MasterLabelPage';
import { CleaningTasksDashboard } from './forge/pages/dashboard/CleaningTasksDashboard';
import { DataCollectionDashboard } from './forge/pages/dashboard/DataCollectionDashboard';
import { MasterLabelUnresolvedDashboard } from './forge/pages/dashboard/MasterLabelUnresolvedDashboard';
import { RemoteDataCollectionDashboard } from './forge/pages/dashboard/RemoteDataCollectionDashboard';
import { ReviewIssuesDashboard } from './forge/pages/dashboard/ReviewIssuesDashboard';
import {
  FORGE_CLEANING_TASK_LIST_PATH,
  FORGE_MASTER_LABEL_PATH,
  FORGE_REMOTE_DC_TASK_LIST_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
  FORGE_REVIEW_ISSUES_DASHBOARD_PATH,
  FORGE_STORE_DC_TASK_LIST_PATH,
  FORGE_SYSTEM_HEALTH_PATH,
  FORGE_UNRESOLVED_TASK_LIST_PATH,
} from './forge/paths';
import { forgeRoutes } from './forge/routes';
import { CandidateDetailPage } from './pages/embeddings/CandidateDetailPage';
import { CandidatesPage } from './pages/embeddings/CandidatesPage';
import EmbeddingDetailPage from './pages/embeddings/EmbeddingDetailPage';
import { EmbeddingsPage } from './pages/embeddings/EmbeddingsPage';
import { OutsSelectionPage } from './pages/embeddings/OutsSelectionPage';
import { GroundTruthAuditPage } from './pages/ground-truth/GroundTruthAuditPage';
import { ModEligibilityPage } from './pages/mod-eligibility/ModEligibilityPage';
import { PogResetModsPage } from './pages/pog-reset/PogResetModsPage';
import { PogResetPage } from './pages/pog-reset/PogResetPage';
import { PogResetTaskDetailPage } from './pages/pog-reset/PogResetTaskDetailPage';
import { StoreDetailsPage } from './pages/store-details/StoreDetailsPage';
import StoresPage from './pages/stores/StoresPage';
import { ImageAuditPageWrapper } from './pages/tasks/ImageAuditPageWrapper';
import { TaskAuditPageWrapper } from './pages/tasks/TaskDetailPageWrapper';
import { TasksPage } from './pages/tasks/TasksPage';

const RootRedirect: React.FC = () => {
  const { hasInternalAccess } = useAuthContext();
  return <Navigate to={hasInternalAccess ? '/stores' : '/embeddings/detail'} replace />;
};

type RouteCapability = 'hasInternalAccess' | 'canReviewIssues' | 'canSeeTaskLists';

const ProtectedRoute: React.FC<{
  element: React.ReactElement;
  capability?: RouteCapability;
}> = ({ element, capability = 'hasInternalAccess' }) => {
  const auth = useAuthContext();

  if (!auth[capability]) {
    return <Navigate to="/embeddings/detail" replace />;
  }

  return element;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '/stores',
    element: <ProtectedRoute element={<StoresPage />} />,
  },
  {
    path: '/stores/:storeName/mod-eligibility',
    element: <ProtectedRoute element={<ModEligibilityPage />} />,
  },
  {
    path: '/pog-reset',
    element: <ProtectedRoute element={<PogResetPage />} />,
  },
  {
    path: '/stores/:storeName/pog-reset',
    element: <ProtectedRoute element={<PogResetPage />} />,
  },
  {
    // Before :taskId so 'mods' isn't captured as a task id.
    path: '/stores/:storeName/pog-reset/mods',
    element: <ProtectedRoute element={<PogResetModsPage />} />,
  },
  {
    path: '/stores/:storeName/pog-reset/:taskId',
    element: <ProtectedRoute element={<PogResetTaskDetailPage />} />,
  },
  {
    path: '/stores/:storeName/:deviceName?',
    element: <ProtectedRoute element={<StoreDetailsPage />} />,
  },
  {
    path: '/embeddings',
    element: <ProtectedRoute element={<EmbeddingsPage />} />,
  },
  {
    path: '/embeddings/detail',
    element: <EmbeddingDetailPage />,
  },
  {
    path: '/tasks',
    element: <ProtectedRoute element={<TasksPage />} />,
  },
  {
    path: '/tasks/:taskId',
    element: <ProtectedRoute element={<TaskAuditPageWrapper />} />,
  },
  {
    path: '/gt/audit/:gtSampleId',
    element: <ProtectedRoute element={<GroundTruthAuditPage />} />,
  },
  {
    path: '/images/:imageId/audit',
    element: <ProtectedRoute element={<ImageAuditPageWrapper />} />,
  },
  {
    path: '/embeddings/candidates',
    element: <ProtectedRoute element={<CandidatesPage />} />,
  },
  {
    path: '/embeddings/candidates/:imageId',
    element: <CandidateDetailPage />,
  },
  {
    path: '/forge',
    element: <Navigate to={FORGE_SYSTEM_HEALTH_PATH} replace />,
  },
  {
    path: FORGE_MASTER_LABEL_PATH,
    element: <ForgeLayout />,
    children: [{ index: true, element: <MasterLabelPage /> }],
  },
  {
    path: FORGE_REMOTE_DC_TOOL_PATH,
    element: <ForgeLayout />,
    children: [{ index: true, element: <MasterDataCollectionPage /> }],
  },
  {
    path: FORGE_UNRESOLVED_TASK_LIST_PATH,
    element: <ForgeLayout />,
    children: [{ index: true, element: <MasterLabelUnresolvedDashboard /> }],
  },
  {
    path: FORGE_STORE_DC_TASK_LIST_PATH,
    element: <ProtectedRoute capability="canSeeTaskLists" element={<ForgeLayout />} />,
    children: [{ index: true, element: <DataCollectionDashboard /> }],
  },
  {
    path: FORGE_REMOTE_DC_TASK_LIST_PATH,
    element: <ProtectedRoute capability="canSeeTaskLists" element={<ForgeLayout />} />,
    children: [{ index: true, element: <RemoteDataCollectionDashboard /> }],
  },
  {
    path: FORGE_CLEANING_TASK_LIST_PATH,
    element: <ProtectedRoute capability="canSeeTaskLists" element={<ForgeLayout />} />,
    children: [{ index: true, element: <CleaningTasksDashboard /> }],
  },
  {
    path: FORGE_REVIEW_ISSUES_DASHBOARD_PATH,
    element: <ProtectedRoute capability="canReviewIssues" element={<ForgeLayout />} />,
    children: [{ index: true, element: <ReviewIssuesDashboard /> }],
  },
  {
    ...forgeRoutes,
    element: <ProtectedRoute element={forgeRoutes.element as React.ReactElement} />,
  },
  {
    ...evalRoutes,
    element: <ProtectedRoute element={evalRoutes.element as React.ReactElement} />,
  },
  {
    path: '/outs-selection/:imageId',
    element: <OutsSelectionPage />,
  },
]);

export const App: React.FC = () => {
  return <RouterProvider router={router} />;
};
